<template>
  <div 
    v-if="isEscalated" 
    class="escalation-banner animate-slide-down mx-6 mb-4 relative overflow-hidden"
  >
    <!-- Banner container con glassmorphism -->
    <div class="relative bg-gradient-to-r from-orange-500/10 via-red-500/10 to-pink-500/10 backdrop-blur-xl border border-orange-200/30 rounded-2xl p-6 shadow-2xl">
      
      <!-- Elementos decorativos animados -->
      <div class="absolute -top-2 -right-2 w-20 h-20 bg-gradient-conic from-orange-400/20 via-red-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse"></div>
      <div class="absolute -bottom-1 -left-1 w-16 h-16 bg-gradient-conic from-yellow-400/15 via-orange-400/15 to-red-400/15 rounded-full blur-lg animate-pulse" style="animation-delay: 0.5s;"></div>
      
      <!-- Contenido principal -->
      <div class="relative z-10 flex items-start space-x-4">
        
        <!-- Icono premium animado -->
        <div class="flex-shrink-0">
          <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg border border-orange-400/30 animate-gentle-bounce">
            <div class="relative">
              <svg class="w-7 h-7 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <!-- Pulse indicator -->
              <div class="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white animate-ping opacity-75"></div>
            </div>
          </div>
        </div>
        
        <!-- Contenido textual -->
        <div class="flex-1 min-w-0">
          <!-- Título principal -->
          <div class="flex items-center space-x-2 mb-2">
            <h3 class="text-lg font-bold text-orange-900">
              🔄 Conversación Escalada a Soporte Humano
            </h3>
            <div class="flex items-center space-x-1">
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span class="text-xs font-semibold text-orange-700 px-2 py-1 bg-orange-100/80 rounded-full border border-orange-200/60">
                ACTIVO
              </span>
            </div>
          </div>
          
          <!-- Descripción -->
          <p class="text-sm text-orange-800 mb-4 leading-relaxed">
            Tu consulta ha sido <strong>escalada</strong> a nuestro equipo de especialistas. 
            Un agente humano con conocimiento técnico especializado se pondrá en contacto contigo pronto.
          </p>
          
          <!-- Métricas en tiempo real -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            
            <!-- Tiempo transcurrido -->
            <div class="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-orange-200/40 shadow-sm">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p class="text-xs font-medium text-orange-700">Escalado hace</p>
                  <p class="text-sm font-bold text-orange-900">{{ escalationDuration }}</p>
                </div>
              </div>
            </div>
            
            <!-- Estado del agente -->
            <div class="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-orange-200/40 shadow-sm">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
                <div>
                  <p class="text-xs font-medium text-orange-700">Agente asignado</p>
                  <p class="text-sm font-bold text-orange-900 flex items-center">
                    {{ assignedAgent || 'Buscando especialista...' }}
                    <div v-if="!assignedAgent" class="ml-2 w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  </p>
                </div>
              </div>
            </div>
            
            <!-- Prioridad -->
            <div class="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-orange-200/40 shadow-sm">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                </svg>
                <div>
                  <p class="text-xs font-medium text-orange-700">Prioridad</p>
                  <p class="text-sm font-bold text-red-700">{{ priority }}</p>
                </div>
              </div>
            </div>
            
          </div>
          
          <!-- Acciones disponibles -->
          <div class="flex flex-wrap items-center gap-3">
            
            <!-- Botón para ver ticket -->
            <button 
              v-if="ticketId"
              @click="$emit('view-ticket', ticketId)"
              class="group inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] border border-blue-500/20"
            >
              <svg class="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>Ver Ticket #{{ ticketId }}</span>
            </button>
            
            <!-- Indicador de respuesta esperada -->
            <div class="inline-flex items-center space-x-2 px-3 py-2 bg-green-100/80 text-green-800 text-xs font-medium rounded-xl border border-green-200/60">
              <svg class="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
              </svg>
              <span>Respuesta en {{ estimatedResponseTime }}</span>
            </div>
            
            <!-- Estado de conexión con el agente -->
            <div class="inline-flex items-center space-x-2 px-3 py-2 bg-orange-100/80 text-orange-800 text-xs font-medium rounded-xl border border-orange-200/60">
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span>{{ agentConnectionStatus }}</span>
            </div>
            
          </div>
          
        </div>
        
        <!-- Botón de cerrar opcional -->
        <div class="flex-shrink-0">
          <button 
            v-if="dismissible"
            @click="$emit('dismiss')"
            class="w-8 h-8 flex items-center justify-center text-orange-500 hover:text-orange-700 hover:bg-orange-100/50 rounded-lg transition-all duration-200 hover:scale-105"
            title="Minimizar banner"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
      </div>
      
      <!-- Barra de progreso sutil -->
      <div class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-200/30 to-red-200/30 rounded-b-2xl overflow-hidden">
        <div class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-b-2xl animate-pulse" :style="`width: ${progressPercentage}%`"></div>
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  isEscalated: {
    type: Boolean,
    required: true
  },
  escalationTime: {
    type: [String, Date, Number],
    default: null
  },
  assignedAgent: {
    type: String,
    default: null
  },
  priority: {
    type: String,
    default: 'Alta'
  },
  ticketId: {
    type: [String, Number],
    default: null
  },
  estimatedResponseTime: {
    type: String,
    default: '10-15 min'
  },
  dismissible: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['view-ticket', 'dismiss']);

// Estado reactivo para actualizaciones en tiempo real
const currentTime = ref(new Date());
let timeInterval = null;

// Computed properties
const escalationDuration = computed(() => {
  if (!props.escalationTime) return '0 min';
  
  const escalated = new Date(props.escalationTime);
  const now = currentTime.value;
  const diffInMinutes = Math.floor((now - escalated) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Menos de 1 min';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min`;
  } else {
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${hours}h`;
    }
  }
});

const agentConnectionStatus = computed(() => {
  if (props.assignedAgent) {
    return 'Agente conectado';
  } else {
    return 'Buscando especialista...';
  }
});

const progressPercentage = computed(() => {
  if (!props.escalationTime) return 0;
  
  const escalated = new Date(props.escalationTime);
  const now = currentTime.value;
  const diffInMinutes = Math.floor((now - escalated) / (1000 * 60));
  
  // Simular progreso: 0% al inicio, 70% a los 15 minutos, 100% a los 30 minutos
  const maxTimeMinutes = 30;
  const progress = Math.min((diffInMinutes / maxTimeMinutes) * 100, 100);
  
  return Math.max(progress, 5); // Mínimo 5% para mostrar algo
});

// Lifecycle
onMounted(() => {
  // Actualizar tiempo cada minuto para duración en tiempo real
  timeInterval = setInterval(() => {
    currentTime.value = new Date();
  }, 60000); // 60 segundos
});

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval);
  }
});
</script>

<style scoped>
@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes gentle-bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-2px);
  }
  60% {
    transform: translateY(-1px);
  }
}

.animate-slide-down {
  animation: slide-down 0.5s ease-out;
}

.animate-gentle-bounce {
  animation: gentle-bounce 2s infinite;
}

.escalation-banner {
  /* Ensure proper stacking context */
  z-index: 10;
}

/* Glassmorphism effects */
.backdrop-blur-xl {
  backdrop-filter: blur(16px);
}

/* Gradient conic fallback */
.bg-gradient-conic {
  background: conic-gradient(from 0deg, var(--tw-gradient-stops));
}
</style>
